import React, { useEffect, useMemo, useState } from "react";

import {
    DashboardOutlined,
    SaveOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    UserOutlined
} from "@ant-design/icons";

import {
    canSyncRehabData,
    getActiveRehabProfileId,
    getRehabHistory,
    saveRehabData,
    deleteRehabData
} from "../../api/rehabTracking";

import {
    getPatientProfiles
} from "../../api/patientProfile";


export default function Berg() {

    const initialAnswers = {
        item1: "",
        item2: "",
        item3: "",
        item4: "",
        item5: "",
        item6: "",
        item7: "",
        item8: "",
        item9: "",
        item10: "",
        item11: "",
        item12: "",
        item13: "",
        item14: ""
    };


    const [answers, setAnswers] =
        useState(initialAnswers);

    const [note, setNote] =
        useState("");

    const [history, setHistory] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);

    const [message, setMessage] =
        useState("");

    const [activeProfile, setActiveProfile] =
        useState(null);


    /*
     * 14 MỤC BERG
     *
     * Website chỉ ghi nhận điểm đã được đánh giá.
     */
    const questions = [
        {
            key: "item1",
            title: "Đứng lên từ tư thế ngồi"
        },
        {
            key: "item2",
            title: "Đứng không có hỗ trợ"
        },
        {
            key: "item3",
            title: "Ngồi không tựa lưng"
        },
        {
            key: "item4",
            title: "Ngồi xuống"
        },
        {
            key: "item5",
            title: "Chuyển từ ghế này sang ghế khác"
        },
        {
            key: "item6",
            title: "Đứng nhắm mắt"
        },
        {
            key: "item7",
            title: "Đứng khép hai chân"
        },
        {
            key: "item8",
            title: "Vươn tay ra phía trước khi đứng"
        },
        {
            key: "item9",
            title: "Nhặt vật từ sàn"
        },
        {
            key: "item10",
            title: "Quay nhìn ra phía sau"
        },
        {
            key: "item11",
            title: "Quay 360 độ"
        },
        {
            key: "item12",
            title: "Đặt luân phiên chân lên bục"
        },
        {
            key: "item13",
            title: "Đứng chân trước chân sau"
        },
        {
            key: "item14",
            title: "Đứng một chân"
        }
    ];


    /*
     * TỔNG ĐIỂM
     */
    const totalScore =
        useMemo(() => {

            return Object.values(
                answers
            ).reduce(
                (
                    total,
                    value
                ) => {

                    if (
                        value === "" ||
                        value === null ||
                        value === undefined
                    ) {
                        return total;
                    }


                    return (
                        total +
                        Number(value)
                    );
                },
                0
            );

        }, [answers]);


    /*
     * KIỂM TRA ĐÃ NHẬP ĐỦ
     */
    const completed =
        useMemo(() => {

            return Object.values(
                answers
            ).every(
                (value) =>
                    value !== "" &&
                    value !== null &&
                    value !== undefined
            );

        }, [answers]);


    /*
     * CHỌN ĐIỂM
     */
    const handleAnswerChange = (
        questionKey,
        value
    ) => {

        setAnswers(
            (prev) => ({
                ...prev,
                [questionKey]:
                    Number(value)
            })
        );
    };


    /*
     * FORMAT NGÀY GIỜ
     */
    const formatDateTime = (value) => {

        if (!value) {

            return {
                date: "",
                time: ""
            };
        }


        const dateObject =
            new Date(value);


        if (
            Number.isNaN(
                dateObject.getTime()
            )
        ) {

            return {
                date: "",
                time: ""
            };
        }


        return {

            date:
                dateObject.toLocaleDateString(
                    "vi-VN"
                ),

            time:
                dateObject.toLocaleTimeString(
                    "vi-VN",
                    {
                        hour: "2-digit",
                        minute: "2-digit"
                    }
                )
        };
    };


    /*
     * LOAD HỒ SƠ ĐANG CHỌN
     */
    const loadActiveProfile =
        async () => {

            try {

                if (!canSyncRehabData()) {

                    setActiveProfile(null);

                    return;
                }


                const activeId =
                    getActiveRehabProfileId();


                if (!activeId) {

                    setActiveProfile(null);

                    return;
                }


                const profiles =
                    await getPatientProfiles();


                const found =
                    Array.isArray(profiles)
                        ? profiles.find(
                            (item) =>
                                Number(
                                    item.profile_id
                                ) ===
                                Number(activeId)
                        )
                        : null;


                setActiveProfile(
                    found || null
                );

            } catch (error) {

                console.error(
                    "Không thể tải hồ sơ:",
                    error
                );


                setActiveProfile(null);
            }
        };


    /*
     * LOAD LỊCH SỬ BERG
     */
    const loadHistory =
        async () => {

            setLoading(true);

            setMessage("");


            try {

                if (!canSyncRehabData()) {

                    setHistory([]);

                    setMessage(
                        "Vui lòng đăng nhập để lưu và xem dữ liệu phục hồi chức năng."
                    );

                    return;
                }


                if (!getActiveRehabProfileId()) {

                    setHistory([]);

                    setMessage(
                        "Vui lòng chọn hồ sơ đang theo dõi trước khi sử dụng công cụ."
                    );

                    return;
                }


                const data =
                    await getRehabHistory(
                        "berg"
                    );


                const formatted =
                    Array.isArray(data)
                        ? data.map(
                            (item) => {

                                const tracking =
                                    item.tracking_data ||
                                    {};


                                const dateTime =
                                    formatDateTime(
                                        item.created_at
                                    );


                                return {

                                    id:
                                        item.health_tracking_id,

                                    score:
                                        Number(
                                            tracking.score ??
                                            0
                                        ),

                                    answers:
                                        tracking.answers ||
                                        {},

                                    note:
                                        tracking.note ||
                                        "",

                                    date:
                                        tracking.date ||
                                        dateTime.date,

                                    time:
                                        tracking.time ||
                                        dateTime.time
                                };
                            }
                        )
                        : [];


                setHistory(
                    formatted
                );

            } catch (error) {

                console.error(
                    "Không thể tải lịch sử Berg:",
                    error
                );


                setHistory([]);


                setMessage(
                    error?.message ||
                    "Không thể tải lịch sử Berg."
                );

            } finally {

                setLoading(false);
            }
        };


    /*
     * LOAD KHI MỞ TRANG
     */
    useEffect(() => {

        loadActiveProfile();

        loadHistory();

    }, []);


    /*
     * LƯU KẾT QUẢ
     */
    const handleSave =
        async () => {

            if (!canSyncRehabData()) {

                alert(
                    "Vui lòng đăng nhập trước khi lưu dữ liệu."
                );

                return;
            }


            if (!getActiveRehabProfileId()) {

                alert(
                    "Vui lòng chọn hồ sơ đang theo dõi trước."
                );

                return;
            }


            if (!completed) {

                alert(
                    "Vui lòng nhập đủ điểm của 14 mục trước khi lưu."
                );

                return;
            }


            const now =
                new Date();


            const trackingData = {

                score:
                    totalScore,

                answers,

                note:
                    note.trim(),

                date:
                    now.toLocaleDateString(
                        "vi-VN"
                    ),

                time:
                    now.toLocaleTimeString(
                        "vi-VN",
                        {
                            hour: "2-digit",
                            minute: "2-digit"
                        }
                    )
            };


            setSaving(true);

            setMessage("");


            try {

                await saveRehabData(
                    "berg",
                    trackingData
                );


                setAnswers({
                    ...initialAnswers
                });

                setNote("");


                await loadHistory();


                setMessage(
                    "Đã lưu kết quả Berg."
                );

            } catch (error) {

                console.error(
                    "Không thể lưu Berg:",
                    error
                );


                setMessage(
                    error?.message ||
                    "Không thể lưu kết quả Berg."
                );

            } finally {

                setSaving(false);
            }
        };


    /*
     * XÓA
     */
    const handleDelete =
        async (id) => {

            const confirmed =
                window.confirm(
                    "Bạn có chắc muốn xóa kết quả Berg này?"
                );


            if (!confirmed) {
                return;
            }


            try {

                await deleteRehabData(
                    id
                );


                setHistory(
                    (current) =>
                        current.filter(
                            (item) =>
                                Number(item.id) !==
                                Number(id)
                        )
                );


                setMessage(
                    "Đã xóa kết quả Berg."
                );

            } catch (error) {

                console.error(
                    "Không thể xóa Berg:",
                    error
                );


                setMessage(
                    error?.message ||
                    "Không thể xóa kết quả Berg."
                );
            }
        };


    return (

        <div className="max-w-screen-xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">


            {/* QUAY LẠI */}

            <a
                href="/tienich/phuc-hoi-chuc-nang"
                className="inline-flex items-center text-emerald-600 font-semibold text-sm mb-4"
            >

                <ArrowLeftOutlined className="mr-2" />

                Phục hồi chức năng

            </a>


            {/* HEADER */}

            <div className="bg-gradient-to-r from-purple-600 to-violet-500 rounded-2xl px-5 py-5 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl">

                        <DashboardOutlined />

                    </div>


                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">

                            PHỤC HỒI CHỨC NĂNG

                        </p>


                        <h1 className="text-[25px] md:text-3xl font-bold">

                            Berg Balance Scale

                        </h1>

                    </div>

                </div>


                <p className="text-[14px] md:text-base mt-4 opacity-90 leading-5 md:leading-normal max-w-3xl">

                    Ghi nhận điểm của 14 hoạt động đánh giá khả năng thăng bằng chức năng.

                </p>

            </div>


            {/* HỒ SƠ */}

            {activeProfile ? (

                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mt-4">

                    <div className="flex items-center">

                        <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">

                            <UserOutlined />

                        </div>


                        <div className="ml-3">

                            <p className="text-[11px] font-bold text-emerald-700 uppercase">

                                Đang ghi dữ liệu cho

                            </p>


                            <p className="font-bold text-gray-900">

                                {activeProfile.profile_name}

                            </p>


                            {activeProfile.relationship && (

                                <p className="text-xs text-gray-500 mt-0.5">

                                    {activeProfile.relationship}

                                </p>

                            )}

                        </div>

                    </div>

                </div>

            ) : (

                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mt-4">

                    <p className="font-semibold text-gray-800 text-sm">

                        Chưa chọn hồ sơ theo dõi

                    </p>


                    <p className="text-[13px] text-gray-600 mt-1">

                        Quay lại trang Phục hồi chức năng để chọn hoặc tạo hồ sơ trước khi lưu dữ liệu.

                    </p>


                    <a
                        href="/tienich/phuc-hoi-chuc-nang"
                        className="inline-block mt-2 text-emerald-600 font-semibold text-sm"
                    >

                        Chọn hồ sơ

                    </a>

                </div>

            )}


            {/* THÔNG BÁO */}

            {message && (

                <div className="bg-gray-50 border rounded-xl px-4 py-3 mt-4 text-[13px] text-gray-600">

                    {message}

                </div>

            )}


            {/* TỔNG ĐIỂM */}

            <div className="bg-white border rounded-2xl p-4 md:p-5 mt-5">

                <div className="flex items-center justify-between">

                    <div>

                        <p className="text-sm text-gray-500">

                            Tổng điểm hiện tại

                        </p>


                        <p className="text-[13px] text-gray-400 mt-1">

                            Nhập điểm đã được đánh giá từ 0 đến 4 cho từng mục.

                        </p>

                    </div>


                    <div className="bg-purple-50 text-purple-700 rounded-xl px-4 py-2 text-center">

                        <p className="text-2xl font-bold">

                            {totalScore}

                        </p>


                        <p className="text-[11px]">

                            / 56 điểm

                        </p>

                    </div>

                </div>

            </div>


            {/* CÁC MỤC */}

            <div className="mt-6">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                    Các mục đánh giá

                </h2>


                <p className="text-gray-500 text-sm mt-1">

                    Ghi lại điểm từ kết quả đánh giá đã thực hiện phù hợp.

                </p>


                <div className="space-y-4 mt-4">

                    {questions.map(
                        (
                            question,
                            questionIndex
                        ) => (

                            <div
                                key={question.key}
                                className="bg-white border rounded-2xl p-4 md:p-5"
                            >

                                <div className="flex items-start">

                                    <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-sm flex-shrink-0">

                                        {questionIndex + 1}

                                    </div>


                                    <div className="ml-3 flex-1">

                                        <p className="font-bold text-gray-800">

                                            {question.title}

                                        </p>

                                    </div>

                                </div>


                                <div className="grid grid-cols-5 gap-2 mt-4">

                                    {[0, 1, 2, 3, 4].map(
                                        (score) => {

                                            const selected =
                                                answers[
                                                    question.key
                                                ] === score;


                                            return (

                                                <button
                                                    key={score}
                                                    type="button"
                                                    onClick={() =>
                                                        handleAnswerChange(
                                                            question.key,
                                                            score
                                                        )
                                                    }
                                                    className={`h-11 rounded-xl border font-bold ${
                                                        selected
                                                            ? "border-purple-500 bg-purple-50 text-purple-700"
                                                            : "border-gray-200 bg-white text-gray-500"
                                                    }`}
                                                >

                                                    {score}

                                                </button>

                                            );
                                        }
                                    )}

                                </div>

                            </div>

                        )
                    )}

                </div>

            </div>


            {/* GHI CHÚ */}

            <div className="bg-white border rounded-2xl p-4 md:p-5 mt-5">

                <label className="block text-sm font-semibold text-gray-700 mb-2">

                    Ghi chú

                    <span className="font-normal text-gray-400 ml-1">

                        (không bắt buộc)

                    </span>

                </label>


                <textarea
                    value={note}
                    onChange={(e) =>
                        setNote(
                            e.target.value
                        )
                    }
                    placeholder="Nhập ghi chú cho lần đánh giá này..."
                    rows={3}
                    className="w-full border rounded-xl px-3 py-3 outline-none focus:border-purple-500 resize-none"
                />


                <button
                    type="button"
                    onClick={
                        handleSave
                    }
                    disabled={
                        saving ||
                        !activeProfile
                    }
                    className="w-full mt-4 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >

                    <SaveOutlined className="mr-2" />

                    {saving
                        ? "Đang lưu..."
                        : "Lưu kết quả Berg"
                    }

                </button>

            </div>


            {/* LỊCH SỬ */}

            <div className="mt-7">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                    Lịch sử đánh giá

                </h2>


                <p className="text-gray-500 text-sm mt-1">

                    Các kết quả Berg của hồ sơ đang được chọn.

                </p>


                {loading ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <p className="text-sm text-gray-500">

                            Đang tải dữ liệu...

                        </p>

                    </div>

                ) : history.length === 0 ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <DashboardOutlined className="text-3xl text-gray-300" />


                        <p className="font-semibold text-gray-700 mt-3">

                            Chưa có kết quả

                        </p>


                        <p className="text-sm text-gray-500 mt-1">

                            Kết quả Berg của hồ sơ này sẽ xuất hiện tại đây.

                        </p>

                    </div>

                ) : (

                    <div className="space-y-3 mt-4">

                        {history.map(
                            (item) => (

                                <div
                                    key={item.id}
                                    className="bg-white border rounded-2xl p-4"
                                >

                                    <div className="flex items-start justify-between">

                                        <div className="flex-1 pr-3">

                                            <div className="flex items-end">

                                                <p className="text-3xl font-bold text-purple-600">

                                                    {item.score}

                                                </p>


                                                <span className="text-sm text-gray-400 ml-1 mb-1">

                                                    / 56 điểm

                                                </span>

                                            </div>


                                            <p className="text-xs text-gray-400 mt-2">

                                                {item.time} - {item.date}

                                            </p>


                                            {item.note && (

                                                <div className="bg-gray-50 rounded-xl px-3 py-2 mt-3">

                                                    <p className="text-[13px] text-gray-500">

                                                        {item.note}

                                                    </p>

                                                </div>

                                            )}

                                        </div>


                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleDelete(
                                                    item.id
                                                )
                                            }
                                            className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0"
                                            aria-label="Xóa kết quả Berg"
                                        >

                                            <DeleteOutlined />

                                        </button>

                                    </div>

                                </div>

                            )
                        )}

                    </div>

                )}

            </div>


            {/* LƯU Ý */}

            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4 mt-7">

                <p className="font-semibold text-gray-800 text-sm">

                    Lưu ý

                </p>


                <p className="text-[13px] text-gray-600 leading-5 mt-1">

                    Công cụ này dùng để ghi nhận và cộng điểm từ kết quả đánh giá Berg đã được thực hiện.

                </p>


                <p className="text-[13px] text-gray-500 leading-5 mt-2">

                    Các hoạt động đánh giá thăng bằng có thể có nguy cơ té ngã.
                    Việc đánh giá nên được thực hiện trong điều kiện phù hợp và có nhân viên chuyên môn khi cần thiết.

                </p>

            </div>

        </div>
    );
}